import {
  addMinutes,
  clockLabel,
  minutesOf,
  spanOffset,
  startWithin,
  type Span,
} from '../api/freeBusy'

/**
 * One participant's occupied time, whoever they are.
 *
 * **Nothing here says teacher, dancer or room.** That is deliberate: a recital
 * wizard and a class planner ask the same question this screen asks, and two
 * implementations of "when is everybody free" would drift — with the second
 * never receiving the first's fixes. The caller decides what a lane means.
 */
export type Lane = {
  id: string
  label: string
  /** A resolved colour, not a token name — lanes are categorical, never the accent. */
  color: string
  busy: Span[]
}

export type AvailabilityDay = {
  date: string
  lanes: Lane[]
  free: Span[]
  /**
   * Why this day is not on offer, when it is not.
   *
   * A day quietly missing from the suggestions looks exactly like a day with no
   * gaps. Saying it is a policy, not an accident, is what makes the rule
   * falsifiable by eye.
   */
  note?: string
}

type Props = {
  days: AvailabilityDay[]
  windowStart: string
  windowEnd: string
  /** Marks the currently chosen start, so the grid and the list agree. */
  picked?: { date: string; start: string } | null
  /**
   * How long the lesson is, so a chosen slot can be drawn at its real length.
   *
   * <b>Without it the selection painted the whole free stretch</b> (D-452).
   * Reported: *"when selecting a time slot make it so its only show how long
   * you are selecting, like 30 min."* A 30-minute lesson in a gap running 5:00
   * to 9:15 filled the row solid from five o'clock, which reads as four hours
   * booked — on the one control whose job is to show what is about to be
   * booked.
   */
  lessonMinutes?: number
  onPick?: (date: string, start: string) => void
}

/** Hour marks across the top, at most one every two hours so they never collide. */
function ticks(windowStart: string, windowEnd: string) {
  const open = Math.ceil(minutesOf(windowStart) / 60)
  const close = Math.floor(minutesOf(windowEnd) / 60)
  const step = close - open > 8 ? 2 : 1
  const out: { hour: number; left: number }[] = []

  for (let hour = open; hour <= close; hour += step) {
    const { left } = spanOffset(
      { start: `${String(hour).padStart(2, '0')}:00`, end: `${String(hour).padStart(2, '0')}:00` },
      windowStart,
      windowEnd,
    )
    out.push({ hour, left })
  }

  return out
}

function dayLabel(date: string): string {
  // Parsed as parts rather than `new Date(date)`, which reads a bare date as
  // UTC and shows the previous day west of Greenwich (D-68).
  const [y, m, d] = date.split('-').map(Number)
  const local = new Date(y, m - 1, d)

  return local.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric' })
}

/**
 * Days down, time across — deliberately not the other way round.
 *
 * A single-day view returns nothing exactly when it is needed: the busiest
 * teacher in the real schedule works 4:00 to 9:15 with no gap at all, so the
 * only answer worth giving spans a fortnight.
 */
export default function AvailabilityGrid({
  days,
  windowStart,
  windowEnd,
  picked,
  lessonMinutes,
  onPick,
}: Props) {
  const marks = ticks(windowStart, windowEnd)

  return (
    <div className="w-full overflow-x-auto">
      <div className="min-w-[34rem] rounded-xl border border-line bg-surface">
        <div className="flex border-b border-line bg-surface-2">
          <div className="w-[5.5rem] shrink-0" />
          <div className="relative h-5 grow">
            {marks.map((mark) => (
              <span
                key={mark.hour}
                className="tabular absolute top-1 text-[10px] text-ink-3"
                style={{ left: `${mark.left}%` }}
              >
                {clockLabel(`${String(mark.hour).padStart(2, '0')}:00`).replace(':00', '')}
              </span>
            ))}
          </div>
        </div>

        {days.map((day) => {
          const shut = day.note !== undefined

          return (
            <div
              key={day.date}
              className={`flex items-center border-b border-line last:border-b-0 ${
                shut ? 'bg-warn-bg' : ''
              }`}
              style={{ height: `${14 + Math.max(day.lanes.length, 1) * 10 + 8}px` }}
            >
              <div className="flex w-[5.5rem] shrink-0 items-center gap-1.5 pl-2.5">
                <span className="text-[12px] font-semibold">{dayLabel(day.date)}</span>
                {shut && (
                  <span className="rounded-full border border-warn px-1 text-[9px] font-bold tracking-[0.06em] text-warn uppercase">
                    out
                  </span>
                )}
              </div>

              <div className="relative h-full grow border-l border-line">
                {shut ? (
                  <span className="absolute inset-0 flex items-center pl-2.5 text-[11px] text-warn italic">
                    {day.note}
                  </span>
                ) : (
                  <>
                    {/*
                      <b>Which block is the chosen one, when two overlap</b>
                      (D-503). The server now emits one block per run of
                      bookable starts and deliberately does not merge them, so
                      two can overlap — room A free 5:00 to 6:00, room B from
                      5:45. A pick landing in the overlap is inside both, and
                      marking both `aria-pressed` announces one time twice. The
                      earliest containing block wins, which is the one whose
                      start the pick was clamped against.
                    */}
                    {(() => {
                      const chosen =
                        picked?.date === day.date
                          ? day.free.findIndex(
                              (s) =>
                                minutesOf(picked.start) >= minutesOf(s.start) &&
                                minutesOf(picked.start) < minutesOf(s.end),
                            )
                          : -1

                      return day.free.map((span, index) => {
                      const { left, width } = spanOffset(span, windowStart, windowEnd)
                      /*
                        <b>Inside the stretch, not equal to its start</b>
                        (D-501). It compared the two starts, which was the same
                        question while a click could only ever choose the start.
                        Now that it can choose 7:00 inside a 5:00 block, that
                        test says "no block is chosen" and takes `aria-pressed`
                        with it — undoing exactly what D-428 was written to fix.
                      */
                      const on = index === chosen

                      if (width <= 0) return null

                      return (
                        <button
                          key={`${span.start}-free`}
                          type="button"
                          /*
                            <b>Where in the stretch she clicked, not the start
                            of it</b> (D-501). This took `span.start` however
                            far along the block the pointer was, so an evening
                            free from 5:00 to 9:15 offered exactly one time —
                            and D-452 drawing the band at its real length is
                            what made that impossible to miss.

                            <b>A keyboard press has no x</b>: `detail` is 0 for
                            Enter and Space, where `clientX` is 0 and would read
                            as the far left. Falling back to the start of the
                            stretch is what this control has always done, so
                            keyboard behaviour is unchanged rather than quietly
                            made worse. Choosing a later time by keyboard is the
                            time field below, which is the real answer for it.
                          */
                          onClick={(e) => {
                            if (!onPick) return

                            // No length means nothing can be clamped against
                            // it, and the band below draws nothing either — so
                            // this defers rather than guessing thirty minutes
                            // and offering a start the save might refuse.
                            if (e.detail === 0 || !lessonMinutes) {
                              onPick(day.date, span.start)
                              return
                            }

                            const box = e.currentTarget.getBoundingClientRect()
                            const ratio = box.width > 0 ? (e.clientX - box.left) / box.width : 0

                            onPick(day.date, startWithin(span, ratio, lessonMinutes))
                          }}
                          /*
                           * <b>Which one she picked was a background colour and
                           * nothing else</b> (D-428) — unannounced to a screen
                           * reader, and with nothing a test could bind to but a
                           * Tailwind class. It had also never once been true:
                           * the caller built the time as `"18:00:00:00"`, so
                           * `on` was false on every day since this shipped.
                           */
                          aria-pressed={on}
                          aria-label={`Free from ${clockLabel(span.start)} to ${clockLabel(span.end)} on ${dayLabel(day.date)}`}
                          /*
                            <b>The block stays the block, whether or not it is
                            chosen</b> (D-452). It used to fill solid when
                            picked, so choosing a 30-minute lesson painted the
                            whole free stretch — 5:00 to 9:15 on a real evening
                            — and read as four hours booked. The lesson itself
                            is drawn separately below, at its own length.
                          */
                          className="absolute top-0 bottom-0 rounded border border-dashed border-accent bg-accent-soft"
                          style={{ left: `${left}%`, width: `${width}%` }}
                        />
                      )
                      })
                    })()}

                    {/*
                      <b>What is actually being booked</b> (D-452), drawn inside
                      the stretch it sits in rather than instead of it: the
                      dashed block still answers "where could this go", and the
                      solid band answers "where is it going".

                      After the free blocks in the DOM so it paints over its own
                      one, and `pointer-events-none` so the block underneath
                      stays the thing you click — the band is a statement, not a
                      second control offering the same act.
                    */}
                    {(() => {
                      if (!picked || picked.date !== day.date || !lessonMinutes) return null

                      const lesson = {
                        start: picked.start,
                        end: addMinutes(picked.start, lessonMinutes),
                      }

                      const { left, width } = spanOffset(lesson, windowStart, windowEnd)
                      if (width <= 0) return null

                      return (
                        <span
                          aria-hidden="true"
                          className="pointer-events-none absolute top-0 bottom-0 rounded bg-accent"
                          style={{ left: `${left}%`, width: `${width}%` }}
                        />
                      )
                    })()}

                    {day.lanes.map((lane, index) =>
                      lane.busy.map((span) => {
                        const { left, width } = spanOffset(span, windowStart, windowEnd)

                        if (width <= 0) return null

                        return (
                          <span
                            key={`${lane.id}-${span.start}`}
                            aria-hidden="true"
                            className="absolute h-2 rounded-[3px]"
                            style={{
                              top: `${5 + index * 10}px`,
                              left: `${left}%`,
                              width: `${width}%`,
                              background: lane.color,
                            }}
                          />
                        )
                      }),
                    )}
                  </>
                )}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
